import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import localStorageAgent from '$/common/utils/localStorageUtil';
import { useLanguageStore } from '$/store/languageStore';

import {
  ASSET_LIBRARY_LAST_TAB_STORAGE_KEY,
  getVisibleAssetLibraryTabs,
  normalizeAssetLibraryTab,
  type AssetLibraryTabKey,
} from './tabs';

interface UseAssetLibraryTabOptions {
  /** 主体库选择场景禁止进入官方资产，但不隐藏官方资产 Tab。 */
  disableOfficialTab?: boolean;
}

/** 返回当前个人或团队账号作用域对应的资产 Tab。 */
function getAccountAssetLibraryTab(isTeamAssetLibrary: boolean): AssetLibraryTabKey {
  return isTeamAssetLibrary ? 'team' : 'personal';
}

/**
 * 写入资产库最后访问 tab；SSR 或浏览器存储异常时不阻塞弹窗渲染。
 */
function writeCachedAssetLibraryTab(tab: AssetLibraryTabKey): void {
  if (typeof window === 'undefined') return;
  try {
    localStorageAgent.setItem(ASSET_LIBRARY_LAST_TAB_STORAGE_KEY, tab);
  } catch {
    // 存储失败不影响资产库入口展示。
  }
}

/**
 * 读取资产库缓存 tab，并按当前账号作用域映射到可见入口。
 */
function readInitialAssetLibraryTab(
  isTeamAssetLibrary: boolean,
  disableOfficialTab: boolean,
): AssetLibraryTabKey {
  if (disableOfficialTab) return getAccountAssetLibraryTab(isTeamAssetLibrary);
  if (typeof window === 'undefined') {
    return normalizeAssetLibraryTab(undefined, isTeamAssetLibrary);
  }

  try {
    const cachedTab = localStorageAgent.getItem(ASSET_LIBRARY_LAST_TAB_STORAGE_KEY);
    const normalizedTab = normalizeAssetLibraryTab(cachedTab, isTeamAssetLibrary);
    if (cachedTab !== normalizedTab) {
      writeCachedAssetLibraryTab(normalizedTab);
    }
    return normalizedTab;
  } catch {
    return normalizeAssetLibraryTab(undefined, isTeamAssetLibrary);
  }
}

/**
 * 管理资产库顶层 tab，可见性跟随个人/团队账号作用域并持久化最后入口。
 */
export function useAssetLibraryTab(
  isTeamAssetLibrary: boolean,
  options?: UseAssetLibraryTabOptions,
) {
  const disableOfficialTab = options?.disableOfficialTab ?? false;
  const languageStore = useLanguageStore((state) => state.languageStore);
  const tabLabels = useMemo(
    () => ({
      personal: languageStore.smartcatTranslationText((value) => value.personal_assets),
      team: languageStore.smartcatTranslationText((value) => value.team_assets),
      official: languageStore.smartcatTranslationText((value) => value.official_assets),
    }),
    [languageStore],
  );
  const visibleTabs = useMemo(
    () => getVisibleAssetLibraryTabs(isTeamAssetLibrary, tabLabels).map((tab) => ({
      ...tab,
      disabled: disableOfficialTab && tab.key === 'official',
    })),
    [disableOfficialTab, isTeamAssetLibrary, tabLabels],
  );
  const [activeTab, setActiveTab] = useState<AssetLibraryTabKey>(() => readInitialAssetLibraryTab(
    isTeamAssetLibrary,
    disableOfficialTab,
  ));

  // 来源约束变化或账号作用域切换时，确保当前 Tab 仍然可用且不污染普通资产库缓存。
  useEffect(() => {
    setActiveTab((currentTab) => {
      const normalizedTab = disableOfficialTab && currentTab === 'official'
        ? getAccountAssetLibraryTab(isTeamAssetLibrary)
        : normalizeAssetLibraryTab(currentTab, isTeamAssetLibrary);
      if (!disableOfficialTab && normalizedTab !== currentTab) {
        writeCachedAssetLibraryTab(normalizedTab);
      }
      return normalizedTab;
    });
  }, [disableOfficialTab, isTeamAssetLibrary]);

  /** 切换可用资产 Tab；主体库打开时静默忽略官方资产目标且不改写普通入口缓存。 */
  const changeActiveTab = useCallback(
    (nextTab: AssetLibraryTabKey) => {
      if (disableOfficialTab && nextTab === 'official') return;
      const normalizedTab = normalizeAssetLibraryTab(nextTab, isTeamAssetLibrary);
      setActiveTab(normalizedTab);
      if (!disableOfficialTab) writeCachedAssetLibraryTab(normalizedTab);
    },
    [disableOfficialTab, isTeamAssetLibrary],
  );

  return {
    activeTab,
    visibleTabs,
    setActiveTab: changeActiveTab,
  };
}
